import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAllActiveSuppliers, groupDuplicateSuppliers, previewSupplierMerge, mergeSuppliers, SupplierMergePreview } from '@/services/suppliers';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { parseDatabaseError } from '@/lib/errors';

export const MergeSuppliersModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const client = useQueryClient();
  const [groupId, setGroupId] = useState('');
  const [primaryId, setPrimaryId] = useState('');
  const [preview, setPreview] = useState<SupplierMergePreview | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [reason, setReason] = useState('Duplicate suppliers created while the supplier list was unavailable.');
  const [message, setMessage] = useState('');
  const { data = [], error, isPending, refetch } = useQuery({
    queryKey: ['suppliers', 'all-active'], queryFn: async () => {
      const suppliers = await fetchAllActiveSuppliers();
      return { data: suppliers, count: suppliers.length };
    },
    select: result => result.data,
  });
  const groups = groupDuplicateSuppliers(data);
  const selected = groups.find(g => g[0].id === groupId) || [];
  const duplicateIds = selected.filter(s => s.id !== primaryId).map(s => s.id);
  const review = useMutation({
    mutationFn: () => previewSupplierMerge(primaryId, duplicateIds),
    onSuccess: result => { setPreview(result); setConfirmed(false); setMessage(''); },
  });
  const merge = useMutation({
    mutationFn: () => {
      if (!preview || !confirmed) throw new Error('Review and confirm the supplier identities first.');
      return mergeSuppliers(primaryId, duplicateIds, preview.preview_token, reason);
    },
    onSuccess: () => {
      setMessage('Merge complete. Purchases, items, payments and stock identifiers have been retained under the primary supplier.');
      setPreview(null); setGroupId(''); setPrimaryId(''); setConfirmed(false);
      for (const key of ['suppliers', 'purchases', 'laptops', 'accessories', 'supplier_returns', 'supplierReturns', 'audit', 'reports']) {
        void client.invalidateQueries({ queryKey: [key] });
      }
    },
  });
  const busy = review.isPending || merge.isPending;
  const resetPreview = () => { setPreview(null); setConfirmed(false); review.reset(); merge.reset(); setMessage(''); };
  return (
    <Modal isOpen title="Merge duplicate suppliers" onClose={() => { if (!busy) onClose(); }} maxWidth="lg">
      <div className="space-y-4 text-sm">
        <p>Choose the duplicate name, then keep the supplier with its phone number. Review all records before merging.</p>
        {message && <p role="status" className="p-3 bg-green-50 text-green-800 rounded">{message}</p>}
        {error && <ErrorAlert message={parseDatabaseError(error)} onRetry={() => { void refetch(); }} />}
        {(review.error || merge.error) && <ErrorAlert message={parseDatabaseError(review.error || merge.error)} />}
        {isPending ? <p>Loading suppliers…</p> : !error && groups.length === 0 ? <p>No duplicate active supplier names found.</p> : null}
        <label className="block font-semibold">Duplicate supplier name
          <select disabled={busy || isPending} className="block w-full p-2 border rounded mt-1" value={groupId} onChange={e => {
            resetPreview(); setGroupId(e.target.value);
            const candidates = groups.find(g => g[0].id === e.target.value) || [];
            const withPhone = candidates.filter(s => (s.phone || '').replace(/\D/g, '').length >= 7);
            setPrimaryId(withPhone.length === 1 ? withPhone[0].id : '');
          }}>
            <option value="">Select a name</option>
            {groups.map(g => <option key={g[0].id} value={g[0].id}>{g[0].name} ({g.length} records)</option>)}
          </select>
        </label>
        {selected.length > 0 && <>
          <fieldset disabled={busy} className="border rounded p-3 space-y-2">
            <legend className="font-semibold px-1">Primary supplier to keep</legend>
            {selected.map(s => <label key={s.id} className="flex gap-2 items-start">
              <input type="radio" name="primarySupplier" value={s.id} checked={primaryId === s.id}
                disabled={(s.phone || '').replace(/\D/g, '').length < 7}
                onChange={() => { resetPreview(); setPrimaryId(s.id); }} />
              <span>{s.name} — {s.phone || 'No phone'}<br />
                <span className="text-xs text-brand-muted">{s.supplier_code || s.id.slice(-8)}{s.city ? ` · ${s.city}` : ''} · Opening balance Rs. {Number(s.opening_balance || 0).toLocaleString('en-PK')}</span>
              </span>
            </label>)}
          </fieldset>
          <button type="button" disabled={!primaryId || busy} className="px-3 py-2 border rounded disabled:opacity-50" onClick={() => { setPreview(null); review.mutate(); }}>
            {review.isPending ? 'Checking records…' : 'Preview merge'}
          </button>
        </>}
        {preview && <div className="space-y-3 border rounded p-3 bg-brand-bg">
          <p className="font-semibold">Move {duplicateIds.length} duplicate record(s) into the selected primary</p>
          <p>{preview.counts.purchases} purchases · {preview.counts.purchase_items} purchase items · {preview.counts.payments} payment entries</p>
          <p>{preview.counts.laptops} laptops · {preview.counts.accessories} accessories · {preview.counts.supplier_returns} supplier returns</p>
          <p>Combined opening balance: <strong>Rs. {Number(preview.combined_opening_balance).toLocaleString('en-PK')}</strong></p>
          <p className="text-xs">The primary phone and contact details stay unchanged. Duplicate suppliers become inactive; their original details remain in the merge history. Purchase totals, item IDs and payment amounts stay unchanged.</p>
          <label className="block">Reason<input disabled={busy} value={reason} onChange={e => setReason(e.target.value)} className="block w-full border rounded p-2 mt-1" /></label>
          <label className="flex gap-2"><input type="checkbox" disabled={busy} checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
            <span>These records represent the same supplier, and the combined opening balance is correct.</span>
          </label>
          <button type="button" disabled={busy || !confirmed || !reason.trim()} onClick={() => merge.mutate()} className="px-4 py-2 bg-brand-green text-white rounded disabled:opacity-50">
            {merge.isPending ? 'Merging…' : 'Merge into primary supplier'}
          </button>
        </div>}
      </div>
    </Modal>
  );
};
